// FinKloz — Vesting Schedule Engine
// Calculates vesting for stock options with 1-year cliff + quarterly vesting.
// Standard: 25% at cliff, then 6.25% per quarter for 3 more years (4 years total).

// ─── Days between two dates ───
function daysBetween(d1, d2) {
  const a = new Date(d1);
  const b = new Date(d2);
  return Math.round((b - a) / 86400000);
}

// ─── Calculate % vested at a given date ───
// vestingStart: vesting commencement date (string YYYY-MM-DD)
// asOfDate: the date to check (string YYYY-MM-DD)
// cliffMonths: months before first vest (default 12)
// totalYears: total vesting period (default 4)
export function percentVested(vestingStart, asOfDate, cliffMonths = 12, totalYears = 4) {
  const days = daysBetween(vestingStart, asOfDate);
  if (days < 0) return 0;

  const cliffDays = cliffMonths * 30.4375; // average days per month
  if (days < cliffDays) return 0; // before cliff

  // After cliff: 25% vests, then 6.25% per quarter
  const quartersAfterCliff = Math.floor((days - cliffDays) / 91.25);
  const totalQuarters = (totalYears - 1) * 4; // 12 quarters after cliff
  const pct = Math.min(0.25 + 0.0625 * quartersAfterCliff, 1.0);

  return Math.round(pct * 10000) / 10000;
}

// ─── Calculate number of vested options ───
export function vestedOptions(totalOptions, vestingStart, asOfDate, cliffMonths = 12) {
  const pct = percentVested(vestingStart, asOfDate, cliffMonths);
  return Math.floor(totalOptions * pct);
}

// ─── Calculate number of unvested options ───
export function unvestedOptions(totalOptions, vestingStart, asOfDate, cliffMonths = 12) {
  return totalOptions - vestedOptions(totalOptions, vestingStart, asOfDate, cliffMonths);
}

// ─── Generate full vesting schedule ───
// Returns array of { date, quarter, vestedPct, vestedQty, cumulativeQty }
export function vestingSchedule(totalOptions, vestingStart, expiryDate) {
  const schedule = [];
  const start = new Date(vestingStart);
  const expiry = new Date(expiryDate);

  // Cliff date (1 year)
  const cliffDate = new Date(start);
  cliffDate.setFullYear(cliffDate.getFullYear() + 1);

  if (cliffDate <= expiry) {
    schedule.push({
      date: cliffDate.toISOString().split('T')[0],
      event: 'Cliff (25%)',
      quarter: 0,
      vestedPct: 0.25,
      vestedQty: Math.floor(totalOptions * 0.25),
      cumulativeQty: Math.floor(totalOptions * 0.25),
    });
  }

  // Quarterly vesting (12 quarters after cliff)
  for (let q = 1; q <= 12; q++) {
    const vestDate = new Date(cliffDate);
    vestDate.setDate(vestDate.getDate() + Math.round(q * 91.25));
    if (vestDate > expiry) break;

    const pct = Math.min(0.25 + 0.0625 * q, 1.0);
    const cumQty = Math.floor(totalOptions * pct);
    const qtyThisQ = cumQty - (schedule.length > 0 ? schedule[schedule.length - 1].cumulativeQty : 0);

    schedule.push({
      date: vestDate.toISOString().split('T')[0],
      event: q === 12 ? 'Final vest (100%)' : `Quarter ${q}`,
      quarter: q,
      vestedPct: pct,
      vestedQty: qtyThisQ,
      cumulativeQty: cumQty,
    });
  }

  return schedule;
}

// ─── FY overlap calculation for expense allocation ───
// Returns the number of days a vesting period overlaps with a fiscal year.
export function fyOverlapDays(vestingStart, vestingEnd, fyStart, fyEnd) {
  const vs = new Date(vestingStart);
  const ve = new Date(vestingEnd);
  const fs = new Date(fyStart);
  const fe = new Date(fyEnd);

  const overlapStart = vs > fs ? vs : fs;
  const overlapEnd = ve < fe ? ve : fe;

  const days = Math.max(0, daysBetween(overlapStart, overlapEnd));
  return days;
}

// ─── Multi-year expense allocation ───
// Allocates total expense across fiscal years based on vesting overlap.
// Returns { 'FY2023': amount, 'FY2024': amount, ... }
export function allocateExpenseByFY(totalExpense, vestingStart, vestingEnd, startFY, endFY) {
  const allocation = {};
  let totalDays = 0;
  const overlaps = {};

  // Calculate overlap for each fiscal year
  for (let fy = startFY; fy <= endFY; fy++) {
    const fyStart = `${fy}-01-01`;
    const fyEnd = `${fy}-12-31`;
    const days = fyOverlapDays(vestingStart, vestingEnd, fyStart, fyEnd);
    overlaps[`FY${fy}`] = days;
    totalDays += days;
  }

  // Allocate proportionally
  if (totalDays > 0) {
    for (const [fy, days] of Object.entries(overlaps)) {
      allocation[fy] = Math.round(totalExpense * days / totalDays * 100) / 100;
    }
  }

  return allocation;
}
